import React, { useMemo, useState } from "react";
import { type CfrSuggestion, useListCfrSuggestions } from "@/lib/api/cfr";
import { DataTable } from "@/components/ui/DataTable/index";
import { type DataTableColumn } from "@/components/ui/DataTable/index";
import { CommodityOptions } from "@/lib/api/commodity";
import { Button } from "@/components/ui/button";
import { formatNumberToDisplay } from "@/lib/api/common";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { IndeterminateCheckbox } from "./components/IndeterminateCheckbox";
import { BulkSendDialog } from "./components/BulkSendDialog";
import { BulkUpdatedReportSendDialog } from "./components/BulkUpdatedReportSendDialog";
import { BulkPurchaseReleaseMarkSendDialog } from "./components/BulkPurchaseReleaseMarkSendDialog";
import { BulkCommodityMarkSendDialog } from "./components/BulkCommodityMarkSendDialog";
import { PurchaseReleaseBreakdown } from "./components/PurchaseReleaseBreakdown";
import type { Table } from "@tanstack/react-table";

export const generateDefaultEmptyInstanceInfo = () => ({
  suggestionId: '',
  selectedCustomerId: '',
  selectedTemplateId: '',
  isDialogOpen: false,

  selectedMarketZoneId: "",
  selectedDestinationId: "",

  commodity: "",
  contractNumber: "",
  contractQuantity: "",
  contractType: "",
});

export interface CfrDocumentTemplateRow {
  id: string;
  created_at: string;

  commodity_type: string;
  crop_year: number;
  destination_id?: string | null;
  customer_destination_id?: string | null;

  version_label?: string;
  document_id: string;

  MarketZone?: { id: string; dtn_market_zone: string; };
  Destination?: { id: string; long_name: string };
}

export function SuggestionSection({ onSuggestionClick, templateData }: { onSuggestionClick: any, templateData: CfrDocumentTemplateRow[] }) {
  const listResult = useListCfrSuggestions();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>("");
  const [selectedSuggestionTab, setSelectedSuggestionTab] = useState<"updated_report" | "customer_mark" | "commodity_mark" | "purchase_release_report" | "purchase_release_breakdown">("customer_mark");
  const [isBulkSendDialogOpen, setIsBulkSendDialogOpen] = useState(false);
  const [selectedSendDialogRows, setSelectedSendDialogRows] = useState<CfrSuggestion[]>([]);

  const list_data = useMemo(() => {
    return listResult?.data?.data?.data || [];
  }, [listResult]);


  const { columns, rows } = useMemo((): { columns: DataTableColumn<CfrSuggestion>[], rows: CfrSuggestion[] } => {
    if (selectedSuggestionTab === "commodity_mark") {
       return {
        columns: [
          {
            id: "selection-box",
            enableFiltering: false,
            enableSorting: false,
            disableRowClick: true,
            header: ({ table }) => (
              <>
                <IndeterminateCheckbox
                  checked={table.getIsAllRowsSelected()}
                  indeterminate={table.getIsSomeRowsSelected()}
                  onChange={table.getToggleAllRowsSelectedHandler()}
                />{' '}
              </>
            ),
            cell: ({ row }) => {
              return (
                <>
                  <IndeterminateCheckbox
                    checked={row.getIsSelected()}
                    disabled={!row.getCanSelect()}
                    indeterminate={row.getIsSomeSelected()}
                    onChange={row.getToggleSelectedHandler()}
                  />
                </>
              );
            },
            size: 50,
          },
          {
            id: "customer_id",
            accessorKey: "customer_id",
            header: "Customer",
            accessorFn: (row) => row.Customer?.long_name || row.original_meta?.customer_name || row.original_meta?.client_id || "",
          },
          {
            id: "commodity",
            accessorKey: "commodity",
            header: "Commodity",
            accessorFn: (row) => {
              const commodity_value = row.commodity || "";
              return CommodityOptions.find((option) => option.value === commodity_value)?.label || commodity_value;
            },
          },
          {
            id: "crop_year",
            accessorKey: "crop_year",
            header: "Crop Year",
          }
        ],
        rows: list_data.filter((row: CfrSuggestion) => row.suggestion_type === "commodity_mark"),
      };

    }


    if (selectedSuggestionTab === "customer_mark") {
      return {
        columns: [
          {
            id: "selection-box",
            enableFiltering: false,
            enableSorting: false,
            disableRowClick: true,
            header: ({ table }) => (
              <>
                <IndeterminateCheckbox
                  checked={table.getIsAllRowsSelected()}
                  indeterminate={table.getIsSomeRowsSelected()}
                  onChange={table.getToggleAllRowsSelectedHandler()}
                />{' '}
              </>
            ),
            cell: ({ row }) => {
              return (
                <>
                  <IndeterminateCheckbox
                    checked={row.getIsSelected()}
                    disabled={!row.getCanSelect()}
                    indeterminate={row.getIsSomeSelected()}
                    onChange={row.getToggleSelectedHandler()}
                  />
                </>
              );
            },
            size: 50,
          },
          {
            id: "customer_id",
            accessorKey: "customer_id",
            header: "Customer",
            accessorFn: (row) => row.Customer?.long_name || row.original_meta?.customer_name || row.original_meta?.client_id || "",
          }
        ],
        rows: list_data.filter((row: CfrSuggestion) => row.suggestion_type === "customer_mark"),
      }
    }

    if (["updated_report", "purchase_release_report"].includes(selectedSuggestionTab)) {
      return {
        columns: [
          {
            id: "selection-box",
            enableFiltering: false,
            enableSorting: false,
            disableRowClick: true,
            header: ({ table }) => (
              <>
                <IndeterminateCheckbox
                  checked={table.getIsAllRowsSelected()}
                  indeterminate={table.getIsSomeRowsSelected()}
                  onChange={table.getToggleAllRowsSelectedHandler()}
                />{' '}
              </>
            ),
            cell: ({ row }) => {
              return (
                <>
                  <IndeterminateCheckbox
                    checked={row.getIsSelected()}
                    disabled={!row.getCanSelect()}
                    indeterminate={row.getIsSomeSelected()}
                    onChange={row.getToggleSelectedHandler()}
                  />
                </>
              );
            },
            size: 50,
          },
          {
            id: "customer_id",
            accessorKey: "customer_id",
            header: "Customer",
            accessorFn: (row) => row.Customer?.long_name || row.original_meta?.customer_name || row.original_meta?.client_id || "",
          },
          {
            id: "customer_destination_id",
            accessorKey: "customer_destination_id",
            header: "Destination",
            accessorFn: (row) => row.Destination?.long_name || "",
          },
{
            id: "crop_year",
            accessorKey: "crop_year",
            header: "Crop Year",
            accessorFn: (row) => row.crop_year || "",
          },
          {
            id: "commodity",
            accessorKey: "commodity",
            header: "Commodity",
            accessorFn: (row) => {
              const commodity_value = row.commodity || "";
              return CommodityOptions.find((option) => option.value === commodity_value)?.label || commodity_value;
            },
            cell: (info) => {
              return info.getValue();
            },
          },
          {
            id: "contract_number",
            accessorKey: "contract_number",
            header: "Contract #",
            cell: ({ row }) => {
              return row.original.contract_number || "";
            },
          },
          {
            id: "contract_quantity",
            accessorKey: "contract_quantity",
            header: "Contract Qty",
            accessorFn: (row) => formatNumberToDisplay(row.contract_quantity || ""),
          },
          {
            id: "shipment_range",
            accessorKey: "shipment_range",
            header: "Shipment Range",
            accessorFn: (row) => row.original_meta?.shipment_range || "",
          },
        ],
        rows: list_data.filter((row: CfrSuggestion) => row.suggestion_type === selectedSuggestionTab),
      }
    }

    return {
      columns: [],
      rows: [],
    }
  }, [selectedSuggestionTab, list_data]);

  // Store table instance to get selected rows for bulk send

  const bulkSendButton = ({ table }: { table: Table<CfrSuggestion> }) => {
    const selectedRows = table.getFilteredSelectedRowModel().rows;
    return (
      <Button
        variant="outline"
        onClick={() => {
          setIsBulkSendDialogOpen(true);
          setSelectedSendDialogRows(selectedRows.map(row => row.original));
        }}
        disabled={selectedRows.length === 0}
      >
        Bulk Send ({selectedRows.length})
      </Button>
    );
  };



  const isLoading = listResult.isLoading;

  return (
    <div className="flex flex-col gap-2">
      <Tabs value={selectedSuggestionTab} onValueChange={(tabValue: string) => setSelectedSuggestionTab(tabValue as any)} className="h-full flex flex-col">
        <TabsList className="flex flex-row gap-2 mb-2">
          <TabsTrigger value="customer_mark">Customer</TabsTrigger>
          <TabsTrigger value="updated_report">Updates</TabsTrigger>
          <TabsTrigger value="commodity_mark">Commodity Report</TabsTrigger>
          <TabsTrigger value="purchase_release_breakdown">Purchase Release Breakdown</TabsTrigger>
        </TabsList>


        <TabsContent value="customer_mark" className="mt-0">
          <DataTable
            stickyHeaders
            tableClassName="h-[calc(50vh-200px)] overflow-y-scroll"
            data={rows}
            columns={columns}
            enableSorting
            enableMultiSort
            enableFiltering
            enableGrouping
            enableColumnVisibility
            enableMultiSelect
            customToolbarButtons={[bulkSendButton]}
            filterStyle="select"
            filterMode="internal"
            emptyMessage={isLoading ? "Loading ..." : "No suggestions found"}
            defaultSorting={[{ id: "customer_id", desc: false }]}
            rowClickable
            onRowClick={onSuggestionClick}
          />
        </TabsContent>
        <TabsContent value="commodity_mark" className="mt-0">
          <DataTable
            stickyHeaders
            tableClassName="h-[calc(50vh-200px)] overflow-y-scroll"
            data={rows}
            columns={columns}
            enableSorting
            enableMultiSort
            enableFiltering
            enableGrouping
            enableColumnVisibility
            enableMultiSelect
            customToolbarButtons={[bulkSendButton]}
            filterStyle="select"
            filterMode="internal"
            emptyMessage={isLoading ? "Loading ..." : "No suggestions found"}
            defaultSorting={[{ id: "customer_id", desc: false }, { id: "commodity", desc: false }]}
            rowClickable
            onRowClick={onSuggestionClick}
          />
        </TabsContent>
        <TabsContent value="updated_report" className="mt-0">
          <DataTable
            stickyHeaders
            tableClassName="h-[calc(50vh-200px)] overflow-y-scroll"
            data={rows}
            columns={columns}
            enableSorting
            enableMultiSort
            enableFiltering
            enableGrouping
            enableColumnVisibility
            enableMultiSelect
            customToolbarButtons={[bulkSendButton]}
            filterStyle="select"
            filterMode="internal"
            emptyMessage={isLoading ? "Loading ..." : "No suggestions found"}
            defaultSorting={[{ id: "customer_id", desc: false }, { id: "commodity", desc: false }, { id: "destination_id", desc: false }]}
            rowClickable
            onRowClick={onSuggestionClick}
          />
        </TabsContent>
        <TabsContent value="purchase_release_breakdown" className="mt-0">
          <PurchaseReleaseBreakdown />
        </TabsContent>
      </Tabs>

      {isBulkSendDialogOpen && selectedSuggestionTab === "customer_mark" && (
        <BulkSendDialog
          open={isBulkSendDialogOpen}
          onOpenChange={setIsBulkSendDialogOpen}
          selectedSuggestions={selectedSendDialogRows}
          templateData={templateData}
        />
      )}
      {isBulkSendDialogOpen && selectedSuggestionTab === "updated_report" && (
        <BulkUpdatedReportSendDialog
          open={isBulkSendDialogOpen}
          onOpenChange={setIsBulkSendDialogOpen}
          selectedSuggestions={selectedSendDialogRows}
          templateData={templateData}
        />
      )}
      {isBulkSendDialogOpen && selectedSuggestionTab === "commodity_mark" && (
        <BulkCommodityMarkSendDialog
          open={isBulkSendDialogOpen}
          onOpenChange={setIsBulkSendDialogOpen}
          selectedSuggestions={selectedSendDialogRows}
          templateData={templateData}
        />
      )}
      {isBulkSendDialogOpen && selectedSuggestionTab === "purchase_release_report" && (
        <BulkPurchaseReleaseMarkSendDialog
          open={isBulkSendDialogOpen}
          onOpenChange={setIsBulkSendDialogOpen}
          selectedSuggestions={selectedSendDialogRows}
          templateData={templateData}
        />
      )}
    </div>
  );
}

export interface CreateInstanceDialogOptions {
  suggestionId: string;
  selectedCustomerId: string;
  selectedTemplateId: string;
  isDialogOpen: boolean;


  selectedMarketZoneId?: string;
  selectedDestinationId?: string;

  commodity: string;
  contractNumber: string;
  contractQuantity: string;
  contractType: string;

  contract_shipping_dates?: string;
  documentTitle?: string;
}
